import { useCallback, useEffect, useRef, useState } from 'react';
import { getKioskToken } from './kiosk-token';
import type { RealtimeEvent } from './types';

export type RealtimeStatus = 'connecting' | 'connected' | 'reconnecting' | 'failed';

interface UseRealtimeOptions {
  kiosk?: boolean;
}

export function useRealtime(
  handler: (event: RealtimeEvent) => void,
  options: UseRealtimeOptions = {},
): { status: RealtimeStatus } {
  const [status, setStatus] = useState<RealtimeStatus>('connecting');
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const wsRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const closedByUserRef = useRef(false);

  const connect = useCallback(() => {
    setStatus(attemptRef.current === 0 ? 'connecting' : 'reconnecting');
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let url = `${protocol}//${window.location.host}/api/realtime/connect`;
    if (options.kiosk) {
      const token = getKioskToken();
      if (!token) {
        setStatus('failed');
        return;
      }
      url += `?kt=${encodeURIComponent(token)}`;
    }
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.addEventListener('open', () => {
      attemptRef.current = 0;
      setStatus('connected');
    });
    ws.addEventListener('message', (e) => {
      try {
        const event = JSON.parse(typeof e.data === 'string' ? e.data : '') as unknown;
        if (event && typeof event === 'object' && 'type' in event) {
          handlerRef.current(event as RealtimeEvent);
        }
      } catch {
        /* drop malformed */
      }
    });
    ws.addEventListener('close', () => {
      if (ws !== wsRef.current) return;
      if (closedByUserRef.current) return;
      attemptRef.current += 1;
      const backoff = Math.min(30000, 1000 * 2 ** Math.min(attemptRef.current, 5));
      if (attemptRef.current > 10) {
        setStatus('failed');
        return;
      }
      setStatus('reconnecting');
      setTimeout(connect, backoff);
    });
    ws.addEventListener('error', () => {
      // Fall through to close handler — close fires immediately after error.
    });
  }, [options.kiosk]);

  useEffect(() => {
    closedByUserRef.current = false;
    connect();
    return () => {
      closedByUserRef.current = true;
      wsRef.current?.close();
    };
  }, [connect]);

  return { status };
}
